import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Landmark, ScrollText, GraduationCap, Drama, Bot, School2, ExternalLink } from 'lucide-react';

const partners = [
  { id: 'cambridge', icon: Landmark, name: 'Cambridge University', tag: 'Examination Center', url: 'https://www.cambridgeenglish.org/exams-and-tests/', display: 'cambridgeenglish.org' },
  { id: 'cervantes', icon: ScrollText, name: 'Instituto Cervantes', tag: 'DELE Certification', url: 'https://toulouse.cervantes.es/fr/diplomes_espagnol/information_diplomes.htm', display: 'cervantes.es' },
  { id: 'ministry', icon: GraduationCap, name: 'Ministère de l\'Éducation', tag: 'République Française' },
  { id: 'newera', icon: Drama, name: 'New Era Academy', tag: 'Theatre Certification', url: 'https://neweraacademy.co.uk', display: 'neweraacademy.co.uk' },
  { id: 'digirobot', icon: Bot, name: 'DigiRobot', tag: 'Robotics & Coding', url: 'https://edurobot.fr', display: 'edurobot.fr' },
  { id: 'eibschools', icon: School2, name: 'EIB Schools', tag: 'International Network', url: 'https://www.eibschools.fr', display: 'eibschools.fr' },
];

export const Partners = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="partners"
      data-testid="partners-section"
      ref={ref}
      className="py-16 lg:py-20 bg-gradient-to-b from-white to-slate-50 border-y border-slate-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-amber-600 font-inter text-sm font-semibold tracking-wider uppercase">
            {t('partners.tag')}
          </span>
          <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mt-3 mb-3">
            {t('partners.title')}
          </h2>
          <p className="font-inter text-slate-500 text-sm sm:text-base max-w-xl mx-auto">
            {t('partners.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-5">
          {partners.map((partner, index) => {
            const Wrapper = partner.url ? 'a' : 'div';
            const wrapperProps = partner.url
              ? {
                  href: partner.url,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                }
              : {};

            return (
              <motion.div
                key={partner.id}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.1 * index }}
              >
                <Wrapper
                  {...wrapperProps}
                  data-testid={`partner-card-${partner.id}`}
                  className={`group bg-white rounded-2xl p-5 lg:p-6 border border-slate-200 hover:border-amber-300 hover:shadow-lg transition-all flex flex-col items-center text-center h-full ${
                    partner.url ? 'cursor-pointer hover:-translate-y-1' : ''
                  }`}
                >
                  <div className="w-14 h-14 rounded-xl bg-slate-50 group-hover:bg-amber-50 flex items-center justify-center mb-3 transition-colors">
                    <partner.icon className="w-7 h-7 text-slate-700 group-hover:text-amber-600 transition-colors" />
                  </div>
                  <h3 className="font-playfair text-sm lg:text-base font-bold text-slate-900 leading-tight mb-1">
                    {partner.name}
                  </h3>
                  <p className="font-inter text-[11px] lg:text-xs text-slate-500 uppercase tracking-wider">
                    {partner.tag}
                  </p>
                  {partner.url && (
                    <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-amber-600 group-hover:text-amber-700">
                      {partner.display}
                      <ExternalLink className="w-3 h-3" />
                    </span>
                  )}
                </Wrapper>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
